import { z } from 'zod';

import {
  AUTHORIZATION_SCOPES,
  AUTHORIZATION_TIERS,
  CAPABILITIES,
  type Capability,
  CLERK_ORG_ROLES,
  type ClerkOrgRole,
  RBAC_POLICIES,
} from '../../libs/auth/rbac';

// ---------------------------------------------------------------------------
// Settings → Roles (#2851)
//
// The management surface for the grant store. Clerk organization roles ARE the
// grants, so this contract carries no policy of its own: the catalog below is
// derived from `RBAC_POLICIES`, which keeps the summary a manager reads and the
// decision a handler makes the same fact.
// ---------------------------------------------------------------------------

export const ClerkOrgRoleSchema = z.enum(CLERK_ORG_ROLES);

/**
 * What one role can do, summarised from the policy rather than described.
 *
 * The scope and tier vocabularies are the policy's own tuples, not literals
 * copied here: a tier added to `rbac.ts` reaches this schema by construction
 * instead of compiling cleanly and then failing inside a parse.
 */
export const RoleCapabilitySummarySchema = z.object({
  capability: z.enum(CAPABILITIES),
  scope: z.enum(AUTHORIZATION_SCOPES),
  tier: z.enum(AUTHORIZATION_TIERS),
});
export type RoleCapabilitySummary = z.infer<typeof RoleCapabilitySummarySchema>;

export const RoleCatalogEntrySchema = z.object({
  role: ClerkOrgRoleSchema,
  /** Capabilities this role holds at any tier above `denied`. */
  granted: z.array(RoleCapabilitySummarySchema),
  /** True while Clerk itself has no such role, so it cannot be assigned yet. */
  missingInClerk: z.boolean(),
});
export type RoleCatalogEntry = z.infer<typeof RoleCatalogEntrySchema>;

export const RoleMemberSchema = z.object({
  userId: z.string().min(1),
  displayName: z.string().min(1),
  /** The Clerk role string as stored, which may be a legacy `org:member`. */
  clerkRole: z.string().min(1),
  /** The policy role it resolves to, or `null` when nothing recognises it. */
  resolvedRole: ClerkOrgRoleSchema.nullable(),
  /** True for the viewer's own membership, which the UI must not let them drop. */
  isSelf: z.boolean(),
});
export type RoleMember = z.infer<typeof RoleMemberSchema>;

export const RolesResponseSchema = z.object({
  members: z.array(RoleMemberSchema).max(200),
  catalog: z.array(RoleCatalogEntrySchema),
  /** Custom roles the policy needs that the Clerk instance does not define. */
  missingRoles: z.array(z.string()),
  /** `unavailable` means Clerk could not be probed — not "nothing is missing". */
  setupStatus: z.enum(['missing', 'ok', 'unavailable']),
  /** True when the membership page capped out, so `members` is a partial list. */
  truncated: z.boolean(),
});
export type RolesResponse = z.infer<typeof RolesResponseSchema>;

/**
 * The body names the member and the new role, and nothing else.
 *
 * The tenant is deliberately absent: it comes from the verified principal the
 * route gate already resolved from `?org`. A body-carried organization would be
 * a second, unchecked way to aim a write, and the two could disagree.
 */
export const RoleUpdateRequestSchema = z.object({
  userId: z.string().trim().min(1),
  role: ClerkOrgRoleSchema,
});
export type RoleUpdateRequest = z.infer<typeof RoleUpdateRequestSchema>;

export const RoleUpdateErrorSchema = z.object({
  error: z.enum([
    'admin_required',
    'invalid_request',
    'org_not_found',
    'role_not_configured',
    'self_demotion',
    'unavailable',
  ]),
});
export type RoleUpdateError = z.infer<typeof RoleUpdateErrorSchema>;

/** The write's success body, so every outcome leaves through a contract. */
export const RoleUpdateSuccessSchema = z.object({ ok: z.literal(true) });
export type RoleUpdateSuccess = z.infer<typeof RoleUpdateSuccessSchema>;

/**
 * The role catalog, derived from the policy map. `missingInClerk` is the doctor
 * probe's finding surfaced where the manager acts on it, so an unassignable role
 * is visible as a typed state rather than a Clerk error on submit.
 */
export function roleCatalog(
  missingRoles: readonly string[],
): readonly RoleCatalogEntry[] {
  return CLERK_ORG_ROLES.map((role) => ({
    role,
    granted: CAPABILITIES.flatMap((capability: Capability) => {
      const grant = RBAC_POLICIES[capability].grants[role];
      return grant.tier === 'denied'
        ? []
        : [{ capability, scope: grant.scope, tier: grant.tier }];
    }),
    missingInClerk: missingRoles.includes(role),
  }));
}

/** Roles a manager may actually assign right now. */
export function assignableRoles(
  missingRoles: readonly string[],
): readonly ClerkOrgRole[] {
  return CLERK_ORG_ROLES.filter((role) => !missingRoles.includes(role));
}
